import React, { useState, useEffect, useCallback } from "react";
import {
  FaFolder,
  FaFolderOpen,
  FaFile,
  FaTrashAlt,
  FaDownload,
  FaSync
} from "react-icons/fa";
import { listFiles, downloadFile, deleteFile } from "../services/files";
import { useAuth } from "react-oidc-context";
import { buildFileTree } from "../utils/buildFileTree";
import "../styles/FileExplorer.css";

// ===== Helpers =====
function getNameFromKey(key = "") {
  const clean = String(key).replace(/\/+$/, "");
  const parts = clean.split("/");
  return parts[parts.length - 1] || clean;
}

function formatBytes(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return "";
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  const kb = b / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "hace unos segundos";
  const min = Math.floor(sec / 60);
  if (min < 60) return `hace ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `hace ${hr} h`;
  const day = Math.floor(hr / 24);
  return `hace ${day} d`;
}

// --- Helpers de Árbol ---
function filterTree(node, term) {
  if (!node) return null;

  const nameMatches = node.name.toLowerCase().includes(term);

  const children = Object.entries(node.children)
    .map(([key, child]) => filterTree(child, term))
    .filter(Boolean)
    .reduce((acc, child) => {
      acc[child.name] = child;
      return acc;
    }, {});

  const files = node.files.filter((file) => {
    const fileName = (file.name || "").toLowerCase();
    const fileKey = (file.key || "").toLowerCase();
    return fileName.includes(term) || fileKey.includes(term);
  });

  if (nameMatches || files.length > 0 || Object.keys(children).length > 0) {
    return { ...node, children, files };
  }
  return null;
}

const TreeNode = ({ node, onRefresh }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="tree-node">
      <div className="node-label" onClick={() => setOpen(!open)}>
        {open ? (
          <FaFolderOpen className="icon-folder open" />
        ) : (
          <FaFolder className="icon-folder" />
        )}
        <span className="folder-name">{node.name}</span>
      </div>

      {open && (
        <div className="node-children">
          {/* Subcarpetas */}
          {Object.values(node.children).map((child, i) => (
            <TreeNode key={i} node={child} onRefresh={onRefresh} />
          ))}

          {/* Archivos */}
          {node.files.map((file, i) => (
            <div key={file.key || i} className="tree-file">
              <div className="file-info">
                <FaFile className="icon-file" />
                <span className="file-name" title={file.key}>
                  {file.name}
                </span>

                <span className="file-meta">
                  <span className="file-size">{formatBytes(file.size)}</span>
                  {file.last_modified && (
                    <span className="file-date" title={file.last_modified}>
                      {timeAgo(file.last_modified)}
                    </span>
                  )}
                </span>
              </div>

              <div className="actions">
                <button
                  onClick={() => downloadFile(file)}
                  title="Descargar"
                  className="btn-icon"
                >
                  <FaDownload />
                </button>
                <button
                  onClick={() => deleteFile(file, onRefresh)}
                  title="Eliminar"
                  className="btn-icon delete"
                >
                  <FaTrashAlt />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Componente Principal ---
const FileExplorer = () => {
  const auth = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

const loadData = useCallback(() => {
  if (!auth.isAuthenticated) return;

  setLoading(true);
  setError(null);

  listFiles()
    .then((data) => {
      const fileList = Array.isArray(data) ? data : [];
      const normalized = fileList.map((f) => ({
        ...f,
        name: f.name || getNameFromKey(f.key),
      }));

      normalized.sort((a, b) => {
        const da = new Date(a.last_modified || 0).getTime();
        const db = new Date(b.last_modified || 0).getTime();
        return db - da;
      });

      setFiles(normalized);
    })
    .catch((err) => {
      console.error("Error cargando archivos:", err);
      setError("No se pudieron cargar los archivos.");
    })
    .finally(() => setLoading(false));
}, [auth.isAuthenticated]);


  useEffect(() => {
    if (auth.isAuthenticated) loadData();
  }, [auth.isAuthenticated, loadData]);


  if (auth.isLoading) return <div className="loading-state">Cargando sesión...</div>;
  if (!auth.isAuthenticated) return <div className="empty-state">Inicia sesión para ver tus archivos.</div>;

  // ✅ Importante: buildFileTree debe construir usando file.key (ruta)
  const fullTree = buildFileTree(files);

  const term = searchTerm.trim().toLowerCase();
  const rootNodes = Object.values(fullTree.children);

  const displayedNodes = term
    ? rootNodes.map((node) => filterTree(node, term)).filter(Boolean)
    : rootNodes;

  return (
    <div className="file-explorer">
      <div className="toolbar">
        <input
          type="text"
          className="search-input"
          placeholder="Buscar evento, fecha o archivo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <button onClick={loadData} className="refresh-btn" disabled={loading}>
          <FaSync className={loading ? "spin" : ""} />{" "}
          {loading ? "Cargando..." : "Actualizar"}
        </button>
      </div>

      {error && <div className="error-msg">{error}</div>}

      <div className="tree-container">
        {displayedNodes.length === 0 && !loading ? (
          <div className="empty-state">
            <p>No se encontraron archivos.</p>
            <small>Prueba ajustando la búsqueda o sube un archivo nuevo.</small>
          </div>
        ) : (
          displayedNodes.map((node, i) => (
            <TreeNode key={i} node={node} onRefresh={loadData} />
          ))
        )}
      </div>
    </div>
  );
};

export default FileExplorer;
